import { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { RetryState } from 'src/app/pages/starterDashboardV2/shared/types';
import type { EnrichedReadyToSharePlatformPostV2 } from 'src/app/shared/api/readyToShare';

export type { RetryState };

export type PostTypeRetryState = Record<EnrichedReadyToSharePlatformPostV2['__typename'], RetryState>;

const INITIAL_RETRY_STATE: PostTypeRetryState = {
	ReadyToShareFacebookPost: 'idle',
	ReadyToShareFacebookGroupsPost: 'idle',
	ReadyToShareFacebookStory: 'idle',
	ReadyToShareInstagramPost: 'idle',
	ReadyToShareInstagramStory: 'idle',
	ReadyToShareRedditGroupsPost: 'idle',
};

/**
 * Placeholder for the backend mutation to trigger asset regeneration.
 * This will be replaced with an actual API call once the backend endpoint is implemented.
 *
 * @param _slug - The petition slug
 * @param _postTypeName - The post type to regenerate assets for
 * @returns Promise that resolves when the regeneration is triggered
 */
async function triggerAssetRegeneration(
	_slug: string,
	_postTypeName: EnrichedReadyToSharePlatformPostV2['__typename'],
): Promise<void> {
	// TODO: Implement actual backend call once endpoint is available
	// This is a placeholder that simulates a successful trigger
	// The backend should:
	// 1. Regenerate preview links for the platform
	// 2. Regenerate AI messages for the post type
	// 3. Regenerate share visuals/images if needed
	return Promise.resolve();
}

type UseRetryPostGenerationProps = {
	slug: string;
};

type UseRetryPostGenerationResult = ModelHookResult<
	{
		retryStates: PostTypeRetryState;
		isRetrying: boolean;
	},
	{
		retryPostType: (postTypeName: EnrichedReadyToSharePlatformPostV2['__typename']) => Promise<void>;
		resetRetryState: (postTypeName: EnrichedReadyToSharePlatformPostV2['__typename']) => void;
	}
>;

/**
 * Hook to manage retry functionality for post generation.
 * Handles triggering asset regeneration and re-polling for specific post types.
 */
export function useRetryPostGeneration({ slug }: UseRetryPostGenerationProps): UseRetryPostGenerationResult {
	const queryClient = useQueryClient();
	const [retryStates, setRetryStates] = useState<PostTypeRetryState>(INITIAL_RETRY_STATE);

	const isRetrying = Object.values(retryStates).some((state) => state === 'retrying');

	const retryPostType = useCallback(
		async (postTypeName: EnrichedReadyToSharePlatformPostV2['__typename']) => {
			// Set retrying state
			setRetryStates((prev) => ({
				...prev,
				[postTypeName]: 'retrying' as const,
			}));

			try {
				// Trigger backend asset regeneration (placeholder)
				await triggerAssetRegeneration(slug, postTypeName);

				await queryClient.invalidateQueries({
					queryKey: ['readyToSharePostsV2', slug, postTypeName],
				});

				// Reset to idle after cache invalidation (polling will handle the actual status)
				setRetryStates((prev) => ({
					...prev,
					[postTypeName]: 'idle' as const,
				}));
			} catch {
				// Set failed state on error
				setRetryStates((prev) => ({
					...prev,
					[postTypeName]: 'failed' as const,
				}));
			}
		},
		[slug, queryClient],
	);

	const resetRetryState = useCallback((postTypeName: EnrichedReadyToSharePlatformPostV2['__typename']) => {
		setRetryStates((prev) => ({
			...prev,
			[postTypeName]: 'idle' as const,
		}));
	}, []);

	return {
		data: {
			retryStates,
			isRetrying,
		},
		actions: {
			retryPostType,
			resetRetryState,
		},
	};
}
